import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Bell, CheckCircle, ClockCounterClockwise, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePermissions } from "@/lib/permissions";

function FollowUpDetail({ fu, onClose, onChanged }) {
  const [remark, setRemark] = useState("");
  const remarks = [...(fu.remarks || [])].reverse();
  const addRemark = async () => {
    if (!remark.trim()) return;
    try {
      await api.post(`/followups/${fu.id}/remark`, { text: remark });
      toast.success("Remark added"); setRemark(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!fu} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fu-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {fu.customer_name}
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary uppercase">{fu.status}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="overline text-muted-foreground">Date</div><div className="font-medium">{fu.next_date}</div></div>
            <div><div className="overline text-muted-foreground">Time</div><div className="font-medium">{fu.next_time || "—"}</div></div>
            <div><div className="overline text-muted-foreground">Channel</div><div className="capitalize">{fu.channel}</div></div>
          </div>
          {fu.notes && (
            <div>
              <div className="overline text-muted-foreground mb-1">Notes</div>
              <div className="text-sm p-3 rounded-md bg-secondary/50 whitespace-pre-wrap">{fu.notes}</div>
            </div>
          )}
          <div>
            <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><ChatCircleDots size={12} />Remarks & history ({remarks.length})</div>
            <div className="flex gap-2 mb-3">
              <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Add a remark…" className="flex-1" data-testid="fu-remark-input" />
              <Button size="sm" onClick={addRemark} data-testid="fu-remark-save">Add</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {remarks.length === 0 && <div className="text-xs text-muted-foreground italic py-4 text-center">No remarks yet — the first note starts the trail.</div>}
              {remarks.map((r, i) => (
                <div key={i} className="text-sm border-l-2 border-primary/40 pl-3 py-1" data-testid={`fu-remark-${i}`}>
                  <div>{r.text}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{r.by_name} · {new Date(r.at).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FollowUps() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ customer_id: "", customer_name: "", next_date: "", next_time: "10:00", channel: "call", notes: "", status: "pending" });

  const load = async () => {
    const [fu, c] = await Promise.all([
      api.get("/followups"),
      api.get("/customers", { params: { scope_mode: "tenant" } }),
    ]);
    setItems(fu.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);
  // Poll lead reminders on the follow-ups page too — reps often live here.
  useEffect(() => {
    const tick = () => api.get("/leads/reminders/due").catch(() => {});
    tick(); const t = setInterval(tick, 60_000); return () => clearInterval(t);
  }, []);

  const save = async () => {
    const cust = customers.find(c => c.id === f.customer_id);
    if (!cust || !f.next_date) return toast.error("Customer & date required");
    await api.post("/followups", { ...f, customer_name: cust.customer_name });
    toast.success("Follow-up scheduled"); setOpen(false); load();
    setF({ customer_id: "", customer_name: "", next_date: "", next_time: "10:00", channel: "call", notes: "", status: "pending" });
  };

  const markDone = async (fu) => {
    await api.put(`/followups/${fu.id}`, { status: "completed" });
    // Sync back to the linked Lead so its reminder dot clears.
    await api.put(`/followups/${fu.id}/sync`).catch(() => {});
    load(); toast.success("Marked done");
  };
  const snooze = async (fu) => {
    const d = new Date(fu.next_date); d.setDate(d.getDate() + 1);
    await api.put(`/followups/${fu.id}`, { next_date: d.toISOString().slice(0,10), status: "snoozed" });
    await api.put(`/followups/${fu.id}/sync`).catch(() => {});
    load(); toast.success("Snoozed +1 day");
  };
  const openFu = async (fu) => {
    // Refetch to keep remarks fresh (list endpoint returns stale copies after inserts).
    try { const r = await api.get(`/followups/${fu.id}`); setSelected(r.data); }
    catch { setSelected(fu); }
  };

  const today = new Date().toISOString().slice(0,10);
  const groups = { today: [], upcoming: [], missed: [], done: [] };
  items.forEach(fu => {
    if (fu.status === "completed") groups.done.push(fu);
    else if (fu.next_date === today) groups.today.push(fu);
    else if (fu.next_date < today) groups.missed.push(fu);
    else groups.upcoming.push(fu);
  });

  const Section = ({ title, list, tone, testId }) => (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-muted-foreground">{title} · {list.length}</div>
      </div>
      <div className="space-y-2">
        {list.map(fu => (
          <button
            key={fu.id}
            onClick={() => openFu(fu)}
            className="w-full text-left p-3 rounded-md border border-border/40 flex items-start gap-3 hover:bg-secondary/40 transition-colors"
            data-testid={`fu-${fu.id}`}
          >
            <div className={`w-8 h-8 rounded-full ${tone} flex items-center justify-center shrink-0`}><Bell size={14} weight="duotone" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{fu.customer_name}</div>
              <div className="text-xs text-muted-foreground">{fu.channel} · {fu.next_date} {fu.next_time}</div>
              {fu.notes && <div className="text-xs mt-1 line-clamp-1">{fu.notes}</div>}
              <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                {fu.remarks?.length > 0 && <span><ChatCircleDots size={10} className="inline mr-0.5" />{fu.remarks.length}</span>}
              </div>
            </div>
            {fu.status !== "completed" && (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); markDone(fu); }} data-testid={`fu-done-${fu.id}`}><CheckCircle size={12} /></Button>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); snooze(fu); }} data-testid={`fu-snooze-${fu.id}`}><ClockCounterClockwise size={12} /></Button>
              </div>
            )}
          </button>
        ))}
        {!list.length && <div className="text-xs text-muted-foreground text-center py-4">All clear</div>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-muted-foreground mb-2">Reminders</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Follow-ups</h1>
          <p className="text-sm text-muted-foreground mt-2">Click any card to open its remark history · quick actions still on the right.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {can("customers.create") && (
            <DialogTrigger asChild><Button className="rounded-full" data-testid="add-followup-button"><Plus size={16} className="mr-2" />Schedule</Button></DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>Schedule Follow-up</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Select value={f.customer_id} onValueChange={(v) => setF({...f,customer_id:v})}>
                <SelectTrigger data-testid="fu-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">DATE</Label><Input type="date" value={f.next_date} onChange={(e) => setF({...f,next_date:e.target.value})} data-testid="fu-date" /></div>
                <div><Label className="text-xs">TIME</Label><Input type="time" value={f.next_time} onChange={(e) => setF({...f,next_time:e.target.value})} /></div>
                <div><Label className="text-xs">CHANNEL</Label>
                  <Select value={f.channel} onValueChange={(v) => setF({...f,channel:v})}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["call","whatsapp","email","meeting"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({...f,notes:e.target.value})} />
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-fu-button">Schedule</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="TODAY" list={groups.today} tone="bg-primary/15 text-primary" testId="fu-today" />
        <Section title="MISSED" list={groups.missed} tone="bg-destructive/15 text-destructive" testId="fu-missed" />
        <Section title="UPCOMING" list={groups.upcoming} tone="bg-secondary text-foreground" testId="fu-upcoming" />
        <Section title="COMPLETED" list={groups.done} tone="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" testId="fu-done" />
      </div>

      {selected && (
        <FollowUpDetail
          fu={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { openFu(selected); load(); }}
        />
      )}
    </div>
  );
}
